import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { FileTextIcon, ZapIcon, BookOpenIcon, AlertTriangleIcon, GiftIcon, ShieldCheckIcon, MailIcon, SettingsIcon, CheckCircle2Icon } from 'lucide-react';
import { CHECKOUT_URL, PRODUCT_IMAGE, COURT_BG, PROBLEMS, VOLUMES, BONUSES, REVIEWS, TESTIMONIALS, OBJECTIONS } from '../data/content';
import { CtaButton } from '../components/CtaButton';
import { Accordion } from '../components/Accordion';
import { BonusCarousel } from '../components/BonusCarousel';
import { Stars } from '../components/Stars';
import { UrgencyBlock } from '../components/UrgencyBlock';
import { SalesPopup } from '../components/SalesPopup';
const fadeUp = {
  initial: {
    opacity: 0,
    y: 24
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-60px'
  },
  transition: {
    duration: 0.5
  }
};
export function Landing() {
  const checkoutRef = useRef<HTMLDivElement>(null);
  const scrollToCheckout = () => {
    checkoutRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const goToCheckout = () => {
    window.location.href = CHECKOUT_URL;
  };
  return <main className="relative min-h-screen w-full overflow-hidden text-white" data-id="element-65">
      {/* Blurred volleyball court background */}
      <div className="fixed inset-0 -z-10 bg-cover bg-center" style={{
      backgroundImage: `url(${COURT_BG})`,
      filter: 'blur(7px)'
    }} aria-hidden="true" data-id="element-66" />
      <div className="fixed inset-0 -z-10 bg-navy-deep/80" aria-hidden="true" data-id="element-67" />

      <div className="mx-auto w-full max-w-md px-4 pb-28 pt-8" data-id="element-68">
        {/* ===== HERO ===== */}
        <motion.section {...fadeUp} className="text-center" data-id="element-69">
          <h1 className="font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight" data-id="element-70">
            Deja de depender de la suerte y{' '}
            <span className="text-gold" data-id="element-71">definí los partidos</span>
          </h1>

          <motion.button type="button" onClick={scrollToCheckout} whileTap={{
          scale: 0.98
        }} className="mx-auto mt-6 block w-4/5" aria-label="Ver oferta" data-id="element-72">
            <img src={PRODUCT_IMAGE} alt="Volley Pro System — Fuerza, prevención y mentalidad de competencia" className="w-full rounded-2xl border border-white/10 shadow-2xl" data-id="element-73" />
          </motion.button>

          <p className="mt-6 text-base leading-relaxed text-slate-200" data-id="element-74">
            El sistema completo de fuerza, prevención, estrategia y psicología
            deportiva, pensado específicamente para jugadores de vóley.
          </p>

          {/* Feature chips */}
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2" data-id="element-75">
            {[{
            icon: <FileTextIcon className="h-4 w-4" data-id="element-76" />,
            label: '100% PDF'
          }, {
            icon: <ZapIcon className="h-4 w-4" data-id="element-77" />,
            label: 'Entrega Inmediata'
          }, {
            icon: <BookOpenIcon className="h-4 w-4" data-id="element-78" />,
            label: '6 Módulos + 6 Bonos'
          }].map(c => <span key={c.label} className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-navy-light/60 px-3 py-1.5 text-xs font-semibold text-gold" data-id="element-79">
                {c.icon}
                {c.label}
              </span>)}
          </div>

          <div className="mt-6" data-id="element-80">
            <CtaButton scrollToCheckout={scrollToCheckout} data-id="element-81" />
          </div>
        </motion.section>

        {/* ===== PROBLEMS ===== */}
        <motion.section {...fadeUp} className="mt-14" data-id="element-82">
          <h2 className="text-center font-display text-2xl font-bold uppercase leading-tight" data-id="element-83">
            Muchos jugadores y jugadoras de volley, pasan por estas situaciones
            a diario
          </h2>
          <ul className="mt-5 space-y-3" data-id="element-84">
            {PROBLEMS.map(p => <li key={p} className="flex items-start gap-3 rounded-xl border border-red-500/20 bg-red-950/20 px-4 py-3" data-id="element-85">
                <AlertTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-400" data-id="element-86" />
                <span className="text-sm text-slate-100" data-id="element-87">{p}</span>
              </li>)}
          </ul>
        </motion.section>

        {/* ===== SOLUTION / VOLUMES ===== */}
        <motion.section {...fadeUp} className="mt-14" data-id="element-88">
          <h2 className="mb-5 text-center font-display text-3xl font-extrabold uppercase text-gold" data-id="element-89">
            Para eso fue hecho este sistema
          </h2>
          <Accordion items={VOLUMES} data-id="element-90" />

          <div className="mt-8" data-id="element-91">
            <CtaButton scrollToCheckout={scrollToCheckout} data-id="element-92" />
          </div>
        </motion.section>

        {/* ===== 5-STAR REVIEWS ===== */}
        <motion.section {...fadeUp} className="mt-14" data-id="element-93">
          <h2 className="mb-5 text-center font-display text-2xl font-bold uppercase" data-id="element-94">
            Lo que dicen quienes ya lo usan
          </h2>
          <div className="space-y-3" data-id="element-95">
            {REVIEWS.map(r => <div key={r.name} className="rounded-xl border border-white/10 bg-navy-light/60 p-4" data-id="element-96">
                <Stars data-id="element-97" />
                <p className="mt-2 text-sm leading-relaxed text-slate-100" data-id="element-98">
                  "{r.text}"
                </p>
                <p className="mt-2 text-xs font-semibold text-gold" data-id="element-99">
                  {r.name} · {r.location}
                </p>
              </div>)}
          </div>
        </motion.section>

        {/* ===== BONUSES ===== */}
        <motion.section {...fadeUp} className="mt-14" data-id="element-100">
          <div className="mb-4 flex items-center justify-center gap-2" data-id="element-101">
            <GiftIcon className="h-6 w-6 text-gold" data-id="element-102" />
            <h2 className="font-display text-2xl font-extrabold uppercase text-white" data-id="element-103">
              También llevate con tu compra:
            </h2>
          </div>
          <div className="mb-4 rounded-xl border border-gold/50 bg-gold/10 px-4 py-2 text-center text-sm font-bold text-gold" data-id="element-104">
            🎁 Estos 6 bonos son GRATIS solo por el día de hoy
          </div>
          <BonusCarousel bonuses={BONUSES} data-id="element-105" />
        </motion.section>

        {/* ===== SHORT TESTIMONIALS ===== */}
        <motion.section {...fadeUp} className="mt-14" data-id="element-106">
          <h2 className="mb-5 text-center font-display text-2xl font-bold uppercase" data-id="element-107">
            Jugadores como vos ya lo probaron
          </h2>
          <div className="space-y-2.5" data-id="element-108">
            {TESTIMONIALS.map(t => <div key={t.name} className="flex items-start gap-3 rounded-xl border border-white/10 bg-navy-light/50 px-4 py-3" data-id="element-109">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold/20 font-bold text-gold" data-id="element-110">
                  {t.name.charAt(0)}
                </div>
                <div data-id="element-111">
                  <div className="flex items-center gap-2" data-id="element-112">
                    <span className="text-sm font-bold text-white" data-id="element-113">
                      {t.name}
                    </span>
                    <span className="text-xs text-slate-400" data-id="element-114">
                      {t.age} · {t.location}
                    </span>
                  </div>
                  <Stars data-id="element-115" />
                  <p className="mt-1 text-sm text-slate-200" data-id="element-116">"{t.text}"</p>
                </div>
              </div>)}
          </div>
        </motion.section>

        {/* ===== FINAL OFFER ===== */}
        <motion.section {...fadeUp} ref={checkoutRef} id="checkout" className="mt-16 scroll-mt-4" data-id="element-117">
          <div className="rounded-3xl border-2 border-gold/60 bg-navy-deep/90 p-5 shadow-2xl" data-id="element-118">
            <h2 className="text-center font-display text-3xl font-extrabold uppercase leading-none text-gold" data-id="element-119">
              Oferta final
            </h2>
            <p className="mt-1 text-center text-sm text-slate-300" data-id="element-120">
              Todo el sistema + los 6 bonos, en un solo pago.
            </p>

            {/* Urgency */}
            <div className="mt-5" data-id="element-121">
              <UrgencyBlock data-id="element-122" />
            </div>

            {/* Order summary */}
            <div className="mt-5 rounded-2xl border border-white/10 bg-navy-light/50 p-4" data-id="element-123">
              <h3 className="mb-3 text-sm font-bold uppercase tracking-wider text-slate-200" data-id="element-124">
                Resumen del pedido
              </h3>
              <ul className="space-y-2 text-sm" data-id="element-125">
                {['6 Volúmenes completos (Rendimiento, Prevención, Retorno, Psicología, Estrategia y Fuerza)', '6 Bonos exclusivos (planillas Excel + guías PDF)', 'Acceso inmediato y de por vida', 'Formato 100% digital (PDF + Excel)'].map(li => <li key={li} className="flex items-start gap-2 text-slate-100" data-id="element-126">
                    <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" data-id="element-127" />
                    <span data-id="element-128">{li}</span>
                  </li>)}
              </ul>

              <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-3 text-sm" data-id="element-129">
                <span className="text-slate-300" data-id="element-130">Bonos (hoy gratis)</span>
                <span className="font-semibold text-emerald-400" data-id="element-131">$0</span>
              </div>

              {/* Price */}
              <div className="mt-4 flex flex-col items-center" data-id="element-132">
                <span className="text-sm font-medium text-slate-400 line-through" data-id="element-133">
                  $48.999
                </span>
                <span className="font-display text-5xl font-extrabold text-gold" data-id="element-134">
                  $19.990
                </span>
                <span className="mt-1 text-xs font-semibold uppercase tracking-wider text-emerald-400" data-id="element-135">
                  Ahorrás más del 50%
                </span>
              </div>
            </div>

            <div className="mt-5" data-id="element-136">
              <CtaButton scrollToCheckout={goToCheckout} label="Quiero definir mis partidos" data-id="element-137" />
            </div>
            <div className="mt-3 flex items-center justify-center gap-2 text-xs text-slate-300" data-id="element-138">
              <ShieldCheckIcon className="h-4 w-4 text-gold" data-id="element-139" />
              Compra segura · Entrega inmediata por correo
            </div>
          </div>
        </motion.section>

        {/* ===== OBJECTIONS ===== */}
        <motion.section {...fadeUp} className="mt-14" data-id="element-140">
          <h2 className="mb-5 text-center font-display text-2xl font-bold uppercase" data-id="element-141">
            Preguntas frecuentes
          </h2>
          <Accordion items={OBJECTIONS.map(o => ({
          emoji: '❓',
          title: o.q,
          items: [o.a]
        }))} data-id="element-142" />
        </motion.section>

        {/* ===== SUPPORT ===== */}
        <motion.section {...fadeUp} className="mt-12 flex flex-col items-center gap-3 text-center" data-id="element-143">
          <div className="flex h-14 w-14 items-center justify-center rounded-full border border-gold/40 bg-navy-light/60" data-id="element-144">
            <SettingsIcon className="h-7 w-7 text-gold" data-id="element-145" />
          </div>
          <p className="text-sm text-slate-300" data-id="element-146">
            ¿Necesitás ayuda? Escribinos a nuestro soporte:
          </p>
          <a href="mailto:EbookDreamShop@gmail.com" className="inline-flex items-center gap-2 text-sm font-bold text-gold underline decoration-gold/40 underline-offset-4" data-id="element-147">
            <MailIcon className="h-4 w-4" data-id="element-148" />
            EbookDreamShop@gmail.com
          </a>
          <p className="mt-6 text-[11px] text-slate-500" data-id="element-149">
            © {new Date().getFullYear()} Volley Pro System · Todos los derechos
            reservados.
          </p>
        </motion.section>
      </div>

      {/* Sticky mobile CTA */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gold/30 bg-navy-deep/95 px-4 py-3 backdrop-blur" data-id="element-150">
        <div className="mx-auto max-w-md" data-id="element-151">
          <button type="button" onClick={scrollToCheckout} className="flex w-full items-center justify-center gap-3 rounded-xl bg-gold px-4 py-3 font-extrabold uppercase text-navy-deep" data-id="element-152">
            <span className="text-sm" data-id="element-153">Quiero definir mis partidos</span>
            <span className="text-sm font-black" data-id="element-154">$19.990</span>
          </button>
        </div>
      </div>

      <SalesPopup data-id="element-155" />
    </main>;
}